'use client';

import { useModalButtonActions } from '@/hooks/useModalButtonActions';
import cx from 'classnames';
import { FC } from 'react';
import { ButtonWithLinkOrAction } from '../buttons/Button.types';
import Link from './Link';

export type UseCaseIntelligenceProps = {
  title: React.ReactNode;
  description: string;
  links: ButtonWithLinkOrAction[];
  media: React.ReactNode;
}

const UseCaseIntelligence: FC<UseCaseIntelligenceProps> = ({ title, description, links = [], media }) => {
  const { modalActionHandler } = useModalButtonActions();

  const header = (
    <div className="flex flex-col md:items-center gap-2 md:gap-3 md:text-center">
      <h2 className="font-500 tracking-tight text-36/42 md:text-54/54">{title}</h2>
      <p className="text-violet-200 text-17/24 max-w-80 text-pretty">{description}</p>

      <ul className="flex gap-1 md:gap-2">
        {links.map((link, index) => {
          return (
            <li key={`use-case-intelligence-link-${link.title?.replace(/\s+/g, '-') ?? index}`}>
              {link.href.length ? (
                <Link
                  className={cx('block button button-size-medium', {
                    'button-primary-white': link.type === 'primary',
                    'button-secondary-violet-300': link.type === 'secondary',
                  })}
                  href={link.href}
                >
                  {link.title}
                </Link>
              ) : (
                <button
                  className={cx('block button button-size-medium', {
                    'button-primary-white': link.type === 'primary',
                    'button-secondary-violet-300': link.type === 'secondary',
                  })}
                  onClick={() => {
                    modalActionHandler(link.action);
                  }}
                >
                  {link.title}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );

  return (
    <div className="bg-violet-900 text-white">
      <div className="px-container-mobile md:px-container-desktop py-4 md:py-15">
        <div className="max-w-grid mx-auto flex flex-col gap-y-3 md:gap-y-8">
          <div className="grid grid-cols-12 gap-x-gutter-mobile md:gap-x-gutter-desktop">
            <div className="col-span-12 md:col-span-10 md:col-start-2 lg:col-span-8 lg:col-start-3">{header}</div>
          </div>
          <div className="grid grid-cols-12 gap-x-gutter-mobile md:gap-x-gutter-desktop">
            <div className="col-span-12 md:col-span-10 md:col-start-2">{media}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UseCaseIntelligence;
